import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { type IWidgetConfigurationSlot } from './IWidgetConfigurationSlot.ts';

/** Registrazione di un fill (`WidgetConfigurationSlotRender`) in uno slot. */
export interface IWidgetConfigurationSlotEntry {
  /** Chiave stabile della registrazione (`useId` del fill). */
  key: string;
  slot: IWidgetConfigurationSlot;
  /** Ordine dentro la card; senza `order` in coda, in ordine di scoperta. */
  order?: number;
  /** Contenuto corrente del fill: aggiornato a ogni render del produttore. */
  node: ReactNode;
}

interface IWidgetConfigurationSlotActions {
  upsert: (entry: IWidgetConfigurationSlotEntry) => void;
  unregister: (entryKey: string) => void;
}

// Context separati: i fill leggono solo le azioni (valore stabile), quindi non
// ri-renderizzano quando cambiano le entries; le entries le consuma il solo
// renderer (`WidgetConfigurationSlotCards`).
const ActionsContext = createContext<IWidgetConfigurationSlotActions | null>(
  null,
);
const EntriesContext = createContext<IWidgetConfigurationSlotEntry[]>([]);

function upsertEntry(entry: IWidgetConfigurationSlotEntry) {
  return (prev: IWidgetConfigurationSlotEntry[]) => {
    const index = prev.findIndex(e => e.key === entry.key);
    // Update in place per le entry note, append per le nuove: la posizione
    // nell'array è l'"ordine di scoperta" usato come tie-break dal renderer.
    if (index === -1) return [...prev, entry];
    const updated = [...prev];
    updated[index] = entry;
    return updated;
  };
}

function removeEntry(entryKey: string) {
  return (prev: IWidgetConfigurationSlotEntry[]) =>
    prev.filter(e => e.key !== entryKey);
}

/**
 * Raccoglie le registrazioni dei `WidgetConfigurationSlotRender` montati nei
 * children. I children arrivano via prop a reference stabile, quindi il
 * setState delle entries non li ri-renderizza (bailout React): si aggiorna
 * solo chi consuma le entries.
 */
export function WidgetConfigurationSlotProvider(props: {
  children: ReactNode;
}) {
  const [entries, setEntries] = useState<IWidgetConfigurationSlotEntry[]>([]);

  const upsert = useCallback((entry: IWidgetConfigurationSlotEntry) => {
    setEntries(upsertEntry(entry));
  }, []);

  const unregister = useCallback((entryKey: string) => {
    setEntries(removeEntry(entryKey));
  }, []);

  const actions = useMemo<IWidgetConfigurationSlotActions>(
    () => ({ upsert, unregister }),
    [upsert, unregister],
  );

  return (
    <ActionsContext.Provider value={actions}>
      <EntriesContext.Provider value={entries}>
        {props.children}
      </EntriesContext.Provider>
    </ActionsContext.Provider>
  );
}

/**
 * Azioni di registrazione per i fill. Fuori dal provider è un errore: il
 * contenuto andrebbe perso in silenzio.
 */
export function useWidgetConfigurationSlotActions(): IWidgetConfigurationSlotActions {
  const actions = useContext(ActionsContext);
  if (!actions) {
    throw new Error(
      'WidgetConfigurationSlotRender richiede un WidgetConfigurationSlotProvider a monte.',
    );
  }
  return actions;
}

/** Entries correnti, per il renderer delle card. */
export function useWidgetConfigurationSlotEntries(): IWidgetConfigurationSlotEntry[] {
  return useContext(EntriesContext);
}
